import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Image from 'next/image';
import dayjs from 'dayjs';
import { BOOKING_TIMEZONE, formatMoney } from '../../booking/model';
import { useBooking } from '../../booking/useBooking';
import { BookingDateField } from './BookingDateField';
import { getVehiclePresentation } from '../../booking/catalog';
import { useReservation } from '../../booking/useReservation';
import { ReservationCheckout } from './ReservationCheckout';

export function BookingWidget({ requestedVehicle, paymentReturnId, onLeavePaymentReturn, onReady }: {
  requestedVehicle?: { id: string } | null;
  paymentReturnId?: string | null;
  onLeavePaymentReturn?: () => Promise<boolean>;
  onReady?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const booking = useBooking();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const reservation = useReservation({
    selection: booking.selection, quote: booking.quote, termsVersion: booking.config?.termsVersion ?? null,
    canContinue: booking.canContinue, message: booking.message, paymentReturnId, onLeavePaymentReturn,
    locale: i18n.language.startsWith('de') ? 'de' : i18n.language.startsWith('en') ? 'en' : 'pl',
    onConflict: code => code === 'NEW_RESERVATION' ? booking.startNewReservation() : booking.retry(),
  });
  const { setVehicleId } = booking;
  const readyReported = useRef(false);
  const ready = reservation.sessionReady && (reservation.locked || booking.canContinue || booking.priceError
    || !!booking.availabilityError || booking.configState.status === 'error' || booking.fleet.status === 'error'
    || booking.accessoriesAllowed && booking.accessories.status === 'error'
    || booking.fleet.status === 'ready' && booking.vehicles.length === 0
    || booking.configState.status === 'ready' && (!booking.config?.bookingEnabled || !booking.config.termsVersion)
    || booking.notice === 'noDates' || booking.notice === 'unconfirmedDates');
  useEffect(() => {
    if (ready && !readyReported.current) { readyReported.current = true; onReady?.(); }
  }, [ready, onReady]);
  const [loadingSlowly, setLoadingSlowly] = useState(false);
  const loading = !reservation.locked && !!booking.loadingStage;
  useEffect(() => {
    setLoadingSlowly(false);
    if (!loading) return;
    const timer = setTimeout(() => setLoadingSlowly(true), 4000);
    return () => clearTimeout(timer);
  }, [loading]);
  useEffect(() => {
    booking.setPaused(!reservation.sessionReady || reservation.locked);
  }, [reservation.sessionReady, reservation.locked, booking.setPaused]);
  const consumedRequest = useRef<typeof requestedVehicle>(undefined);
  useEffect(() => {
    if (requestedVehicle && consumedRequest.current !== requestedVehicle) {
      consumedRequest.current = requestedVehicle;
      if (!reservation.locked) setVehicleId(requestedVehicle.id);
    }
  }, [requestedVehicle, setVehicleId, reservation.locked]);
  useEffect(() => {
    if (reservation.recoveredPayload) {
      const { offerId, ...value } = reservation.recoveredPayload;
      booking.restore({ ...value, offer: offerId });
      setCheckoutOpen(true);
    }
  }, [reservation.recoveredPayload, booking.restore]);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (paymentReturnId !== undefined && reservation.locked) {
      document.getElementById('booking-checkout')?.scrollIntoView({ block: 'start' });
    }
  }, [paymentReturnId, reservation.locked]);
  const returnToBooking = () => {
    setCheckoutOpen(false);
    requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({ block: 'start' });
      sectionRef.current?.focus({ preventScroll: true });
    });
  };
  const total = booking.price ? formatMoney(booking.price.totalMinor, i18n.language) : t('booking.pendingPrice');
  const accessoryCatalog = booking.accessories.status === 'ready' ? booking.accessories.data : [];
  const missingAccessoryIds = booking.accessoryIds.filter(id => !accessoryCatalog.some(extra => extra.id === id));
  const fleetError = booking.fleet.status === 'error' ? booking.fleet.error : null;
  const confirmationError = !!booking.availabilityError || booking.priceError || !!fleetError
    || booking.accessoriesAllowed && booking.accessories.status === 'error';
  const unconfirmedAvailability = !!booking.selection && booking.availability === 'unknown' && !booking.searching;
  const returnTime = booking.range
    ? dayjs(booking.range.endsAt).tz(BOOKING_TIMEZONE).format('DD/MM/YYYY, HH:mm')
    : t('booking.returnPending');
  const continueBooking = () => {
    if (!booking.canContinue || reservation.locked) return;
    booking.confirmSelection();
    setCheckoutOpen(true);
    requestAnimationFrame(() => {
      document.getElementById('booking-checkout')?.scrollIntoView({ block: 'start' });
      document.getElementById('customer-name')?.focus({ preventScroll: true });
    });
  };

  return (
    <section id="booking" className="booking-panel" ref={sectionRef} aria-labelledby="booking-heading" tabIndex={-1}>
      <div className="section-heading">
        <span className="eyebrow">{t('booking.start')}</span>
        <h2 id="booking-heading">{t('booking.heading')}</h2>
        <span className="booking-step">{t('booking.step')}</span>
      </div>
      {!reservation.locked && <>
      <fieldset className="offer-fieldset" disabled={reservation.locked}>
        <legend>{t('booking.duration')}</legend>
        <div className="offer-selector" style={{ gridTemplateColumns: `repeat(${Math.max(1, booking.offers.length)}, minmax(0,1fr))` }}>
          {booking.offers.map(offer => (
            <label key={offer} className={`offer-option${booking.offer === offer ? ' is-selected' : ''}`}>
              <input type="radio" name="rental-offer" value={offer} checked={booking.offer === offer}
                onChange={() => { booking.setOffer(offer); booking.setAccessoryIds([]); }} />
              <span>{t(`booking.offers.${offer}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <p className="field-hint" id="offer-hint">{t(`booking.offerHints.${booking.offer}`)}</p>

      <div className="booking-fields">
        <BookingDateField id="booking-date" label={t('booking.pickupDate')} value={booking.date}
          minDate={booking.today} maxDate={booking.maxDate} onChange={booking.setDate}
          disabled={reservation.locked}
          describedBy="offer-hint range-status" />
        <label className="booking-field" htmlFor="booking-time">
          <span>{t('booking.pickupTime')}</span>
          <select id="booking-time" value={booking.time}
            disabled={booking.offer === 'weekend' || reservation.locked} onChange={event => booking.setTime(event.target.value)}
            aria-describedby="offer-hint range-status">
            {booking.timeOptions.map(time => <option key={time} value={time}>{time}</option>)}
          </select>
        </label>
      </div>
      {booking.offer === 'day' && (
        <fieldset className="return-fieldset">
          <legend className="sr-only">{t('booking.returnTime')}</legend>
          <div className="booking-fields">
            <BookingDateField id="booking-return-date" label={t('booking.returnDate')} value={booking.returnDate}
              minDate={booking.date} maxDate={booking.maxDate} onChange={booking.setReturnDate}
              disabled={!booking.date || booking.date === 'invalid' || reservation.locked} describedBy="offer-hint range-status" />
            <label className="booking-field" htmlFor="booking-return-time">
              <span>{t('booking.returnHour')}</span>
              <select id="booking-return-time" value={booking.returnTime}
                disabled={!booking.date || booking.date === 'invalid' || reservation.locked} onChange={event => booking.setReturnTime(event.target.value)}
                aria-describedby="offer-hint range-status">
                <option value="" disabled>{t('booking.selectTime')}</option>
                {booking.timeOptions.map(time => <option key={time} value={time}>{time}</option>)}
              </select>
            </label>
          </div>
        </fieldset>
      )}
      {booking.range && (
        <div className="return-time">
          {booking.offer === 'day'
            ? <span>{t('booking.billableDays', { count: booking.billableDays })}</span>
            : <><span>{t('booking.returnTime')}</span><strong>{returnTime}</strong></>}
        </div>
      )}
      <div id="range-status" aria-live="polite">
        {booking.notice && (
          <p className="booking-notice">
            {t(`booking.defaults.${booking.notice}`)}
            {['noDates', 'unconfirmedDates'].includes(booking.notice)
              && <>{' '}<button type="button" className="text-button" onClick={booking.retry}>{t('booking.retry')}</button></>}
          </p>
        )}
        {booking.rangeResult.status === 'blocked' && <p className="booking-notice">{t('booking.rulesMissing')}</p>}
        {booking.date && booking.rangeResult.status === 'invalid' && (
          <p className="booking-error" role="alert">
            {t(`booking.rangeErrors.${booking.rangeResult.code}`)}
            {booking.canAdjustPickup && <>{' '}<button type="button" className="text-button"
              onClick={booking.adjustPickup}>{t('booking.defaults.adjust')}</button></>}
          </p>
        )}
      </div>

      <fieldset className="vehicle-fieldset" disabled={reservation.locked} aria-busy={booking.fleet.status === 'loading'}>
        <legend>{t('booking.vehicle')}</legend>
        {booking.searching && <p role="status">{t('booking.defaults.searching')}</p>}
        {booking.fleet.status === 'loading' && <p role="status">{t('booking.loadingFleet')}</p>}
        {fleetError && (
          <div className="booking-error" role="alert">
            <p>{t('booking.apiError')}</p>
            <button type="button" className="text-button" onClick={booking.retry}>{t('booking.retry')}</button>
          </div>
        )}
        {booking.fleet.status === 'ready' && booking.vehicles.length === 0 && (
          <p className="booking-notice">{t('booking.emptyFleet')}</p>
        )}
        <div className="vehicle-grid">
          {booking.vehicles.map(vehicle => {
            const status = booking.availabilityFor(vehicle.id);
            const rentalPrice = booking.rentalPriceFor(vehicle.id);
            const presentation = getVehiclePresentation(vehicle);
            const next = status === 'unavailable' ? booking.nextAvailabilityFor(vehicle.id) : null;
            return (
              <div key={vehicle.id} className={`vehicle-option${vehicle.id === booking.vehicleId ? ' is-selected' : ''}`}>
              <label className={`vehicle-card${vehicle.id === booking.vehicleId ? ' is-selected' : ''}`}>
                <input type="radio" name="vehicle" value={vehicle.id} checked={vehicle.id === booking.vehicleId}
                  onChange={() => booking.setVehicleId(vehicle.id)} />
                {presentation ? (
                  <Image src={presentation.image} width={128} height={112}
                    sizes="128px" alt={vehicle.name} className="vehicle-image" loading="lazy" />
                ) : <span className="vehicle-image-placeholder">{t('booking.photoPending')}</span>}
                <span className="vehicle-details">
                  <strong>{vehicle.name}</strong>
                  <span className={`availability availability--${status}`}>{t(`booking.availability.${status}`)}</span>
                  <span>{rentalPrice !== null ? formatMoney(rentalPrice, i18n.language) : t('booking.pendingPrice')}</span>
                </span>
              </label>
              {next && (
                <div className="vehicle-next-slot" aria-live="polite">
                  <p>{next.status === 'available'
                    ? t('booking.nextSlot.available', { date: dayjs(next.range.startsAt).tz(BOOKING_TIMEZONE).format('DD/MM/YYYY'),
                      time: dayjs(next.range.startsAt).tz(BOOKING_TIMEZONE).format('HH:mm') })
                    : t(`booking.nextSlot.${next.status}`)}</p>
                  {next.status === 'available' && (
                    <button type="button" className="text-button" onClick={() => booking.useNextSlot(vehicle.id)}>
                      {t('booking.nextSlot.choose')}
                    </button>
                  )}
                  {(next.status === 'error' || next.status === 'none') && (
                    <button type="button" className="text-button" onClick={booking.retryNextAvailability}>{t('booking.retry')}</button>
                  )}
                </div>
              )}
              </div>
            );
          })}
        </div>
      </fieldset>
      {booking.date && booking.range && !booking.searching && (
        <p className={`field-hint${booking.availability === 'error' ? ' booking-error' : ''}`} role="status">
          {booking.availability === 'unavailable' && booking.hasAvailableAlternative
            ? t('booking.defaults.chooseAlternative') : t(`booking.availability.${booking.availability}`)}
          {booking.availability === 'error' && <button type="button" className="text-button" onClick={booking.retry}>{t('booking.retry')}</button>}
        </p>
      )}

      <details className="booking-extras">
        <summary>{t('booking.accessories')}</summary>
        {!booking.accessoriesAllowed && <p className="field-hint">{t('booking.accessoriesHint')}</p>}
        {booking.accessories.status === 'loading' && <p role="status">{t('booking.loadingFleet')}</p>}
        {booking.accessories.status === 'error' && (
          <p className="booking-error" role="alert">{t('booking.apiError')} <button type="button" className="text-button" onClick={booking.retry}>{t('booking.retry')}</button></p>
        )}
        {booking.accessories.status === 'ready' && booking.accessories.data.length === 0 && <p>{t('booking.noAccessories')}</p>}
        {booking.accessories.status === 'ready' && missingAccessoryIds.length > 0 && (
          <p className="booking-error" role="alert">
            {t('checkout.errors.ACCESSORY_UNAVAILABLE')}{' '}
            <button type="button" className="text-button" disabled={reservation.locked}
              onClick={() => booking.setAccessoryIds(booking.accessoryIds.filter(id => !missingAccessoryIds.includes(id)))}>
              {t('checkout.removeMissingAccessories')}
            </button>
          </p>
        )}
        {booking.accessories.status === 'ready' && booking.accessories.data.map(accessory => (
          <label className="accessory-option" key={accessory.id}>
            <input type="checkbox" disabled={!booking.accessoriesAllowed || reservation.locked
              || (!booking.accessoryIds.includes(accessory.id) && booking.accessoryAvailabilityFor(accessory.id) === 'unavailable')}
              checked={booking.accessoryIds.includes(accessory.id)}
              onChange={event => booking.setAccessoryIds(event.target.checked
                ? [...booking.accessoryIds, accessory.id]
                : booking.accessoryIds.filter(id => id !== accessory.id))} />
            <span>{accessory.name}</span>
            {booking.accessoriesAllowed && booking.range && (
              <span className="availability">{t(`booking.availability.${booking.accessoryAvailabilityFor(accessory.id)}`)}</span>
            )}
          </label>
        ))}
      </details>

      <label className="booking-field booking-message" htmlFor="booking-message">
        <span>{t('booking.messageLabel')}</span>
        <textarea id="booking-message" rows={3} maxLength={255} value={booking.message} disabled={reservation.locked}
          placeholder={t('booking.messagePlaceholder')} aria-describedby="message-count"
          onChange={event => booking.setMessage(event.target.value.slice(0, 255))} />
      </label>
      <p id="message-count" className="field-hint">{booking.message.length}/255</p>

      <section className="price-summary" aria-labelledby="price-heading" aria-busy={booking.priceLoading}>
        <h3 id="price-heading">{t('booking.summary')}</h3>
        <label className="booking-field" htmlFor="booking-payment">
          <span>{t('reservation.paymentType')}</span>
          <select id="booking-payment" value={booking.paymentType} disabled={reservation.locked}
            onChange={event => booking.setPaymentType(event.target.value === 'full' ? 'full' : 'deposit')}>
            <option value="deposit">{t('reservation.depositOption')}</option>
            <option value="full">{t('reservation.fullOption')}</option>
          </select>
        </label>
        <dl>
          <div><dt>{t('booking.rental')}</dt><dd>{booking.price ? formatMoney(booking.price.rentalMinor, i18n.language) : '—'}</dd></div>
          <div><dt>{t('booking.accessories')}</dt><dd>{booking.price ? formatMoney(booking.price.accessoriesMinor, i18n.language) : '—'}</dd></div>
          <div className="price-total"><dt>{t('booking.total')}</dt><dd>{total}</dd></div>
          <div><dt>{t('booking.deposit')}</dt><dd>{booking.price ? formatMoney(booking.price.refundableDepositMinor, i18n.language) : '—'}</dd></div>
          <div><dt>{t('booking.payNow')}</dt><dd>{booking.price ? formatMoney(booking.price.payableNowMinor, i18n.language) : '—'}</dd></div>
        </dl>
        {!booking.price && (
          <p className="field-hint" role="status">
            {confirmationError ? t('booking.apiError')
              : booking.loadingStage ? t(`booking.loading.${booking.loadingStage}`)
              : unconfirmedAvailability ? t('booking.availability.unknown') : t('booking.quoteMissing')}
            {booking.priceError && <button type="button" className="text-button" onClick={booking.retry}>{t('booking.retry')}</button>}
          </p>
        )}
        <button className="booking-button primary-action" type="button"
          disabled={!booking.canContinue || reservation.locked}
          aria-describedby={!booking.canContinue && !reservation.locked ? 'checkout-status' : undefined} onClick={continueBooking}>
          {loading ? t('booking.loading.action') : t('booking.continue')} <span aria-hidden="true">→</span>
        </button>
        {(!booking.canContinue && !reservation.locked) && (
          <p id="checkout-status" className="field-hint" role="status">
            {booking.configState.status === 'error' ? t('checkout.configUnavailable')
              : booking.configState.status === 'loading' ? t('checkout.loadingConfig')
              : !booking.config?.bookingEnabled || !booking.config.termsVersion ? t('checkout.disabled')
              : confirmationError ? t('booking.apiError')
              : booking.loadingStage ? t(`booking.loading.${booking.loadingStage}`)
              : unconfirmedAvailability ? t('booking.availability.unknown')
              : t('checkout.confirmSelection')}
            {loadingSlowly && <>{' '}{t('booking.loading.slow')}</>}
            {(booking.configState.status === 'error' || confirmationError || unconfirmedAvailability)
              && <button type="button" className="text-button" onClick={booking.retry}>{t('booking.retry')}</button>}
          </p>
        )}
      </section>
      </>}
      {(checkoutOpen || reservation.locked) && (
        <ReservationCheckout reservation={reservation} booking={booking} onReturnToBooking={returnToBooking} />
      )}
    </section>
  );
}
